import React, {useState} from "react";
import {useFormContext} from "react-hook-form";
import {Collapse} from "@mui/material";
import {Box, Chip, Colors, IconApproved, Text} from "@bounce/shared/ui";
import {CardPaymentsRetryFlow} from "../../../../apis/requests/payment-flows/types";
import {IconSliders} from "../../../../assets";
import {Button} from "../../../../ui/components";
import {getPolicySummary} from "../helpers";
import FormSection from "../../flow-form/FormSection";
import {findDefaultFlow} from "../../flow-form/helpers";
import {FromFeatureFlagChip, VariantCardHeader} from "../../flow-form/VariantCardParts";
import {formToPolicy, getDefaultControlRetryPolicy, policyToForm} from "./helpers";
import {PermittedHoursBlock, WeekdayBlock} from "./HoursBlocks";
import PeakDaysBlock from "./PeakDaysBlock";
import PolicyBasicsBlock from "./PolicyBasicsBlock";
import ScheduleBlock from "./ScheduleBlock";
import {FormValues} from "./schema";

const NO_FLAG_TEXT = "Select a feature flag to load its variants.";
const RETRY_POLICY_TITLE = "Retry policy:";
const EDIT_POLICY_LABEL = "Edit policy";
const DONE_LABEL = "Done";
const RESET_LABEL = "Reset to default - control variant";
const TOGGLE_ICON_SIZE = 16;
const TOGGLE_BUTTON_WIDTH = 128;
const EXPAND_TRANSITION_MS = 200;
const POLICY_ROW_HEIGHT = 32;

const VARIANTS_HELP = "Set when each variant may retry a failed charge, by day and local hour.";

type VariantCardProps = {
  index: number;
  expanded: boolean;
  onToggle: () => void;
  defaultFlow: CardPaymentsRetryFlow | undefined;
};

const VariantCard = ({index, expanded, onToggle, defaultFlow}: VariantCardProps) => {
  const {watch, setValue} = useFormContext<FormValues>();
  const variant = watch(`variants.${index}`);

  const resetToDefaultControl = () =>
    setValue(`variants.${index}.policy`, policyToForm(getDefaultControlRetryPolicy(defaultFlow)), {shouldDirty: true});

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        p: 2,
        border: `1px solid ${expanded ? Colors.blue._400 : Colors.tertiary._300}`,
        transition: `border-color ${EXPAND_TRANSITION_MS}ms ease`,
        borderRadius: "10px",
        backgroundColor: Colors.bw.white,
      }}
    >
      <VariantCardHeader index={index} name={variant.name} weight={variant.weight} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) max-content",
          columnGap: 2,
          borderTop: `1px solid ${Colors.tertiary._300}`,
          pt: 1.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: expanded ? "column" : "row",
            alignItems: expanded ? "stretch" : "flex-start",
            columnGap: 2,
          }}
        >
          <Box sx={{display: "flex", alignItems: "center", minHeight: expanded ? 0 : POLICY_ROW_HEIGHT, flexShrink: 0}}>
            <Text size={14} variant="interDisplay600" sx={{color: Colors.grey._900}}>
              {RETRY_POLICY_TITLE}
            </Text>
          </Box>
          <Collapse in={!expanded} sx={expanded ? undefined : {flex: 1, minWidth: 0}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", minHeight: POLICY_ROW_HEIGHT}}>
              {getPolicySummary(formToPolicy(variant.policy)).map((item) => (
                <Chip key={item} size_="small" variant_="inherit" label={item} />
              ))}
            </Box>
          </Collapse>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, pt: 1}}>
              <PolicyBasicsBlock variantIndex={index} />
              <PermittedHoursBlock variantIndex={index} />
              <ScheduleBlock variantIndex={index} />
              <WeekdayBlock variantIndex={index} />
              <PeakDaysBlock variantIndex={index} />
              <Box>
                <Button buttonType="text" size="small" onClick={resetToDefaultControl}>
                  {RESET_LABEL}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </Box>
        <Box sx={{display: "flex", flexDirection: "column", justifyContent: "flex-end"}}>
          <Box sx={{position: "sticky", bottom: 0, backgroundColor: Colors.bw.white}}>
            <Button
              buttonType="outlined-primary"
              size="small"
              onClick={onToggle}
              aria-expanded={expanded}
              startIcon={
                expanded ? (
                  <IconApproved width={TOGGLE_ICON_SIZE} height={TOGGLE_ICON_SIZE} />
                ) : (
                  <IconSliders width={TOGGLE_ICON_SIZE} height={TOGGLE_ICON_SIZE} />
                )
              }
              sx={{width: TOGGLE_BUTTON_WIDTH}}
            >
              {expanded ? DONE_LABEL : EDIT_POLICY_LABEL}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const VariantsSection = ({flows}: {flows: CardPaymentsRetryFlow[]}) => {
  const {watch} = useFormContext<FormValues>();
  const featureFlag = watch("feature_flag");
  const variants = watch("variants");
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const defaultFlow = findDefaultFlow(flows);

  return (
    <FormSection
      title="Variants"
      help={featureFlag ? VARIANTS_HELP : NO_FLAG_TEXT}
      action={featureFlag && <FromFeatureFlagChip />}
    >
      {variants.map((variant, index) => (
        <VariantCard
          key={`${featureFlag}-${variant.name}`}
          index={index}
          expanded={expandedIndex === index}
          onToggle={() => setExpandedIndex(expandedIndex === index ? null : index)}
          defaultFlow={defaultFlow}
        />
      ))}
    </FormSection>
  );
};

export default VariantsSection;
